import React, { useState, useMemo } from 'react'
import { FaSortUp, FaSortDown, FaSort, FaChevronRight, FaChevronDown } from 'react-icons/fa'
import { formatEur } from './networkUtils'

const PROCESS_FAMILIES = [
  { key: 'all', label: 'All' },
  { key: 'machining', label: 'Machining' },
  { key: 'sheet_metal', label: 'Sheet Metal' },
  { key: 'other', label: 'Other' },
]

const MACHINING_AXES = ['material', 'size', 'tolerance_band', 'axis_tier', 'complexity_tier']
const SHEET_METAL_AXES = ['material', 'size', 'gauge_band', 'bend_class', 'complexity_tier']
const OTHER_AXES = ['method', 'material', 'size', 'tolerance_band', 'complexity_tier']
const ALL_AXES = ['method', 'material', 'size', 'tolerance_band', 'complexity_tier']

function getAvailableAxes(processFamily) {
  switch (processFamily) {
    case 'machining': return MACHINING_AXES
    case 'sheet_metal': return SHEET_METAL_AXES
    case 'other': return OTHER_AXES
    default: return ALL_AXES
  }
}

export default function FamiliesView({ parts, families }) {
  const [processFamily, setProcessFamily] = useState('all')
  const [activeAxes, setActiveAxes] = useState(['material', 'size'])
  const [sortCol, setSortCol] = useState('spend')
  const [sortDir, setSortDir] = useState('desc')
  const [expandedRows, setExpandedRows] = useState(new Set())

  const availableAxes = getAvailableAxes(processFamily)

  // Filter parts by process family
  const filteredParts = useMemo(() => {
    if (processFamily === 'all') return parts
    return parts.filter(p => p.process_family === processFamily)
  }, [parts, processFamily])

  // Build dynamic families based on active axes
  const dynamicFamilies = useMemo(() => {
    const groups = {}
    filteredParts.forEach(part => {
      const keyParts = activeAxes.map(ax => part[ax] || 'unknown')
      const key = keyParts.join('__')
      if (!groups[key]) {
        groups[key] = {
          key,
          label: keyParts.map(k => k.replace(/_/g, ' ')).join(' · '),
          axes: Object.fromEntries(activeAxes.map((ax, i) => [ax, keyParts[i]])),
          parts: [],
          suppliers: new Set(),
          spend: 0,
        }
      }
      groups[key].parts.push(part)
      groups[key].spend += part.total_spend || 0
      part.suppliers.forEach(s => groups[key].suppliers.add(s.name))
    })

    return Object.values(groups).map(g => ({
      ...g,
      partsCount: g.parts.length,
      suppliersCount: g.suppliers.size,
    }))
  }, [filteredParts, activeAxes])

  // Sort
  const sorted = useMemo(() => {
    return [...dynamicFamilies].sort((a, b) => {
      let av, bv
      switch (sortCol) {
        case 'parts': av = a.partsCount; bv = b.partsCount; break
        case 'suppliers': av = a.suppliersCount; bv = b.suppliersCount; break
        case 'spend': av = a.spend; bv = b.spend; break
        default: av = a.label; bv = b.label; break
      }
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv))
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [dynamicFamilies, sortCol, sortDir])

  const handleSort = (col) => {
    if (col === sortCol) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('desc') }
  }

  const toggleExpand = (key) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleAxis = (ax) => {
    setActiveAxes(prev =>
      prev.includes(ax) ? prev.filter(a => a !== ax) : [...prev, ax]
    )
    setExpandedRows(new Set())
  }

  return (
    <div className="space-y-4">
      {/* Process family selector */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500 uppercase">Process:</span>
        {PROCESS_FAMILIES.map(pf => (
          <button
            key={pf.key}
            onClick={() => { setProcessFamily(pf.key); setActiveAxes(getAvailableAxes(pf.key).slice(0, 2)); setExpandedRows(new Set()) }}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              processFamily === pf.key
                ? 'bg-[#1e5045] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {pf.label}
            {pf.key !== 'all' && (
              <span className="ml-1 opacity-70">
                ({parts.filter(p => pf.key === 'all' || p.process_family === pf.key).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Axis toggles */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-gray-500 uppercase">Group by:</span>
        {availableAxes.map(ax => (
          <button
            key={ax}
            onClick={() => toggleAxis(ax)}
            className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
              activeAxes.includes(ax)
                ? 'bg-[#1e5045]/10 border-[#1e5045] text-[#1e5045]'
                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-400'
            }`}
          >
            {ax.replace(/_/g, ' ').replace('band', '').replace('tier', '').trim()}
          </button>
        ))}
      </div>

      {/* Families table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">
            {sorted.length} Part Families
          </span>
          <span className="text-xs text-gray-400">
            {filteredParts.length} parts total
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="w-8 px-2"></th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Family</th>
                <SortTh col="parts" label="Parts" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <SortTh col="suppliers" label="Suppliers" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <SortTh col="spend" label="Spend" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {sorted.map(fam => (
                <React.Fragment key={fam.key}>
                  <tr className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer group">
                    <td className="px-2" onClick={(e) => { e.stopPropagation(); toggleExpand(fam.key) }}>
                      <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] border transition-colors ${
                        expandedRows.has(fam.key) ? 'bg-[#1e5045] text-white border-[#1e5045]' : 'bg-gray-50 text-gray-400 border-gray-200 group-hover:border-gray-400'
                      }`}>
                        {expandedRows.has(fam.key) ? <FaChevronDown size={8} /> : <FaChevronRight size={8} />}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-medium text-gray-800 capitalize">{fam.label}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{fam.partsCount}</td>
                    <td className="px-3 py-2 text-right">
                      <span className={`font-medium ${fam.suppliersCount >= 5 ? 'text-red-600' : fam.suppliersCount >= 3 ? 'text-amber-600' : 'text-green-600'}`}>
                        {fam.suppliersCount}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-[#1e5045]">{formatEur(fam.spend)}</td>
                  </tr>
                  {expandedRows.has(fam.key) && (
                    <tr>
                      <td colSpan={5} className="p-0">
                        <ExpandedFamilyRow parts={fam.parts} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function ExpandedFamilyRow({ parts }) {
  const sorted = [...parts].sort((a, b) => (b.total_spend || 0) - (a.total_spend || 0))
  return (
    <div className="mx-4 mb-3 ml-9 border-l-3 border-[#1e5045] rounded-r-lg bg-gray-50 overflow-hidden">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="bg-gray-100">
            <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase text-[9px]">Part</th>
            <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase text-[9px]">Tolerance</th>
            <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase text-[9px]">Suppliers</th>
            <th className="px-3 py-1.5 text-right font-semibold text-gray-500 uppercase text-[9px]">Spend</th>
          </tr>
        </thead>
        <tbody>
          {sorted.slice(0, 20).map(part => (
            <tr key={part.pn} className="border-b border-gray-100 last:border-0 hover:bg-white/50">
              <td className="px-3 py-1.5">
                <div className="font-mono font-medium text-gray-700">{part.pn}</div>
                {part.description && <div className="text-[10px] text-gray-400 truncate max-w-[180px]">{part.description}</div>}
              </td>
              <td className="px-3 py-1.5 text-gray-500">
                {part.tightest_mm ? `±${part.tightest_mm.toFixed(3)}mm` : '—'}
                {part.tolerance_outlier && <span className="ml-1 text-red-500 text-[9px]">⚠ outlier</span>}
              </td>
              <td className="px-3 py-1.5">
                <div className="flex flex-wrap gap-1">
                  {part.suppliers.map(s => (
                    <span key={s.name} className={`inline-block px-2 py-0.5 rounded text-[10px] ${
                      part.suppliers.length > 1 ? 'bg-amber-100 text-amber-800' : 'bg-[#1e5045]/10 text-[#1e5045]'
                    }`}>
                      {s.name.length > 20 ? s.name.slice(0, 20) + '…' : s.name}
                      <span className="opacity-70 ml-1">{formatEur(s.spend)}</span>
                    </span>
                  ))}
                </div>
              </td>
              <td className="px-3 py-1.5 text-right font-medium text-[#1e5045]">{formatEur(part.total_spend)}</td>
            </tr>
          ))}
          {parts.length > 20 && (
            <tr><td colSpan={4} className="px-3 py-1.5 text-center text-gray-400 text-[10px]">
              + {parts.length - 20} more parts
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function SortTh({ col, label, sortCol, sortDir, onSort }) {
  const active = sortCol === col
  return (
    <th
      className="px-3 py-2 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
      onClick={() => onSort(col)}
    >
      <span className="flex items-center justify-end gap-1">
        {label}
        {active
          ? sortDir === 'asc' ? <FaSortUp size={10} className="text-[#1e5045]" /> : <FaSortDown size={10} className="text-[#1e5045]" />
          : <FaSort size={10} className="text-gray-300" />
        }
      </span>
    </th>
  )
}
